<template>
  <Dialog
    visible
    header="Classes"
    modal
    :style="{ width: '90vw', height: '90vh' }"
    :closable="false"
    :dismissable-mask="false"
    block-scroll
    :draggable="false"
  >
    <template #default>
      <VdForm
        class="add-classes-form-page"
        :form="form"
        :form-changed="formChanged"
        :allow-delete="!!cohortId"
        title="Classes"
        :back-link="backLink"
        :save-loading="saveLoading"
        :loading="queryLoading"
        @submit="saveForm"
        @cancel="onDialogClose"
      >
        <div v-if="classChoices.length" class="class-selection">
          <VdFormField prop="class">
            <VdSelect v-model="selectedClassTechnicalName" :choices="classChoices" placeholder="Add a class" multiple>
              <template #item="{ item }">
                <div :class="['class-selection-option', { locked: item.locked }]">
                  <VdTooltip
                    v-if="!!item.locked"
                    :content="`This class is already used by the layer '${item.locked}'`"
                    orientation="right"
                    alignment="end"
                  >
                    <VdIcon store="fa" name="lock" />
                    <span>{{ item.label }}</span>
                  </VdTooltip>
                  <span v-else>{{ item.label }}</span>
                </div>
              </template>
            </VdSelect>
          </VdFormField>
        </div>

        <div v-for="override in form.treatments" :key="override.classId" style="margin-bottom: 1rem">
          <!-- VT.ShopManagementClass must be hidden in the UI -->
          <CohortFormClassItem
            v-if="override.classId !== 'VT.ShopManagementClass'"
            :cohort-id="cohortId"
            :clazz="classesByTechnicalName[override.classId]"
            :override-instances="override.instances"
            :model-value="override"
            :can-delete-class="form.treatments.length > 1"
            :overridden-values="instanceValuesByClass[override.classId]"
            @update:model-value="updateOverriddenClassValues"
            @delete="() => onDeleteClass(override.classId)"
          />

          <div v-if="classesByTechnicalName[override.classId].dynamicInstances" class="add-instance-button-wrapper">
            <vd-button
              type="secondary"
              skin="blue"
              icon-left="fa:add"
              class="add-instance-button"
              @click="onAddInstance(override.classId)"
            >
              Add instance
            </vd-button>
          </div>
        </div>
      </VdForm>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdForm, VdFormField, VdSelect, VdTooltip } from "@voodoo.io/voodoo-ui";
import { computed, nextTick, reactive, ref, watch } from "vue";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";

import { v4 as uuidv4 } from "uuid";

import {
  AbtestType,
  CohortInputOverride,
  CohortTreatment,
  useCohortFormPageQuery,
  useCohortFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import useFormTracking from "@/hooks/form-tracking";
import useMessages from "@/hooks/messages";
import links from "@/router/links";
import { objectifyArray } from "@/utils";
import { mapCohortUpdatePayload } from "@/utils/mapCohortUpdatePayload";

import CohortFormClassItem from "../../CohortsList/children/CohortForm/components/CohortFormClassItem/CohortFormClassItem.vue";
import Dialog from "primevue/dialog";

const router = useRouter();
const route = useRoute();
const { gameId, abtestId, cohortId, segmentId, classId, versionId } = route.query as {
  gameId: string;
  abtestId: string;
  cohortId: string;
  segmentId: string;
  classId: string;
  versionId: string;
};

const { confirm } = useMessages();
const selectedTab = ref("");

interface DynamicCohortInput {
  name: string;
  description: string;
  allocationRate: number;
  treatments: CohortInputOverride[];
}

/**
 * Define the form
 */
const form = reactive<DynamicCohortInput>({
  name: "",
  description: "",
  allocationRate: 0,
  treatments: [],
});

/**
 * Enable form tracking
 */
const { formChanged, resetTracking: resetFormTracking } = useFormTracking(form);

/**
 * Try to fetch the cohort from its id.
 * If we have a cohort, update the form title and fill the form
 */
const { result, loading: queryLoading } = useCohortFormPageQuery({
  gameId,
  versionId,
  abtestId,
});

const cohorts = computed(() => result.value?.game?.version?.abtest?.cohorts ?? []);
const layerId = computed(() => result.value?.game?.version?.abtest?.layerId ?? undefined);
const layers = computed(() => result.value?.game?.version?.layers ?? []);
const cohort = computed(() => cohorts.value.find(({ id }) => id === cohortId));
const classes = computed(() => result.value?.game?.version?.classes ?? []);
const treatment = computed(() => cohort.value?.treatments?.find((t) => t.segmentId === segmentId));

const classesByTechnicalName = computed(() =>
  Object.fromEntries(classes.value.map((clazz) => [clazz.technicalName, clazz]))
);

const instanceValuesByClass = computed(() => {
  if (!cohort.value || cohort.value?.treatments?.length === 0 || !treatment.value?.classesOverrides) {
    return {};
  }

  return objectifyArray(
    treatment.value?.classesOverrides.map((override) => ({
      technicalName: override.class.technicalName,
      instances: override.instances,
    })),
    ["technicalName", "instances", ["id", "values", ["technicalName", "*"]]]
  );
});

/**
 * Watch the cohort changes and fill the form with its values
 */
watch(
  cohort,
  (watchedCohort) => {
    if (watchedCohort) {
      form.name = watchedCohort.name;
      form.description = watchedCohort.description;
      form.allocationRate = watchedCohort.allocationRate;

      const currentTreatment = watchedCohort.treatments?.find((t) => t.segmentId === segmentId);

      // Init value for overridden classes
      form.treatments =
        currentTreatment?.classesOverrides?.map((override) => {
          const classTechnicalName = override.class.technicalName;
          const classDefinition = classesByTechnicalName.value[classTechnicalName];
          const overriddenValues = instanceValuesByClass.value[classTechnicalName] ?? {};

          return {
            classId: classTechnicalName,
            instances: (classDefinition?.instances ?? []).map((inst) => ({
              id: inst.id,
              enabled: overriddenValues[inst.id]
                ? override.instances.find((i) => i.id === inst.id)?.enabled ?? true
                : inst.enabled,
              values: inst.values.map(({ technicalName: attrTechnicalName, defaultValue }) => ({
                technicalName: attrTechnicalName,
                value: overriddenValues[inst.id]?.[attrTechnicalName]?.value ?? defaultValue,
              })),
            })),
          };
        }) ?? [];

      /* always display the first class when arriving on the form */
      if (form.treatments.length) {
        if (classId) {
          selectedTab.value = classId;
        } else {
          selectedTab.value = form.treatments[0].classId;
        }
      }

      resetFormTracking();
    }
  },
  {
    immediate: true,
  }
);

/*
 * Generate the class choices.
 * Prevent displaying the class if it is already present
 */
const classChoices = computed(() =>
  classes.value
    ?.filter(({ technicalName }) => !form.treatments?.find((item) => item.classId === technicalName))
    .filter(({ technicalName }) => technicalName !== "VT.ShopManagementClass") // Filter Shop class
    .map(({ displayName, technicalName, lockedByLayer }) => {
      /**
       * Checking if `lockedByLayer` info is present and if it matches the current layerId.
       * If it's the case then let's return the layer name, otherwise don't send the `locked` info
       */
      let disabled = false;

      const locked =
        !!lockedByLayer && lockedByLayer !== layerId.value
          ? layers.value.find((l) => l.id === lockedByLayer)?.name
          : undefined;
      disabled = (lockedByLayer && lockedByLayer !== layerId.value) || false;

      return {
        label: displayName,
        value: technicalName,
        locked,
        disabled,
      };
    })
);

/**
 * When a class is selected, the class is added to list
 */
const selectedClassTechnicalName = ref("");

watch(selectedClassTechnicalName, async (watchedSelectedClassTechnicalName) => {
  const firstWatchedValue = watchedSelectedClassTechnicalName[0];
  if (!firstWatchedValue) {
    return;
  }
  const selectedClass = classes.value.find(({ technicalName }) => technicalName === firstWatchedValue);

  if (!selectedClass) {
    throw new Error(`unable to find the class ${firstWatchedValue}`);
  }
  const overriddenClass = instanceValuesByClass.value[selectedClass.technicalName];

  form.treatments = [
    ...form.treatments,
    {
      classId: selectedClass.technicalName,
      instances: selectedClass.instances.map((instance) => {
        const overriddenInstance = overriddenClass ? overriddenClass[instance.id] : null;
        const instanceDefaultValuesByAttribute = Object.fromEntries(
          instance.values.map(({ technicalName, defaultValue }) => [technicalName, defaultValue])
        );
        return {
          id: instance.id,
          enabled: instance.enabled,
          values: instance.values.map(({ technicalName: attrTechnicalName }) => {
            const overriddenValue = overriddenInstance
              ? overriddenInstance[attrTechnicalName].value
              : instanceDefaultValuesByAttribute[attrTechnicalName];
            return {
              technicalName: attrTechnicalName,
              value: overriddenValue,
            };
          }),
        };
      }),
    },
  ];

  selectedTab.value = selectedClass.technicalName;
  await nextTick();
  selectedClassTechnicalName.value = "";
});

/**
 * Remove the class from the list
 */
const onDeleteClass = async (technicalName: string) => {
  const confirmed = await confirm("Do you really want to remove this class from the list?");
  if (confirmed) {
    form.treatments = form.treatments.filter((item) => item.classId !== technicalName);
    if (form.treatments.length) {
      selectedTab.value = form.treatments[form.treatments.length - 1].classId;
    }
  }
};

const updateOverriddenClassValues = (value: CohortInputOverride) => {
  form.treatments = form.treatments.map((item) => (item.classId === value.classId ? value : item));
};

const onAddInstance = (id: string) => {
  form.treatments = (form?.treatments || []).map((item) =>
    item.classId === id
      ? {
          ...item,
          instances: [
            ...item.instances,
            {
              id: uuidv4(),
              enabled: true,
              values: classesByTechnicalName.value[id].attributes.map(
                ({ technicalName, isArrayType, formFieldType }) => {
                  let value: string | boolean | string[] = isArrayType ? [] : "";
                  value = formFieldType === "boolean" ? false : value;
                  return {
                    technicalName,
                    value,
                  };
                }
              ),
            },
          ],
        }
      : item
  );
};

/**
 * Define the location to return after save/deletion/cancel
 */
const backLink: RouteLocationRaw = {
  name: links.games.abtests.dynamicTreatment,
  params: {
    gameId,
    abtestId,
    forceReload: "true",
    routingCohortId: cohortId,
    routingSegmentId: segmentId,
  },
};

const onDialogClose = () => {
  router.push(backLink);
};

/**
 * Save the cohort with the following payload
 */
const { mutate: save, loading: saveLoading } = useCohortFormPageSaveMutation({});

const saveForm = async () => {
  const otherCohortSegments = cohort.value?.treatments.filter((c) => c.segmentId !== segmentId) ?? [];
  const otherCohortTreatments =
    otherCohortSegments.length > 0 ? mapCohortUpdatePayload(otherCohortSegments as unknown as CohortTreatment[]) : [];

  let treatmentTypes = treatment.value ? [...treatment.value.treatmentTypes] : [];
  if (!treatmentTypes.includes(AbtestType.Class)) {
    treatmentTypes.push(AbtestType.Class);
  }

  if (form.treatments.length === 1 && form.treatments[0].classId === "VT.ShopManagementClass") {
    treatmentTypes = treatmentTypes.filter((type) => type !== AbtestType.Class);
  }

  // Self-heal (mapCohortUpdatePayload parity): an OFFER-typed treatment with no offers is rejected by the
  // backend's ensureTreatmentIsValid, so strip the type when the arm is empty.
  if (!treatment.value?.offers?.length) {
    treatmentTypes = treatmentTypes.filter((type) => type !== AbtestType.Offer);
  }

  let rank = 1;
  if (treatment.value?.rank) {
    rank = treatment.value.rank;
  } else if (otherCohortSegments.length > 0) {
    rank = otherCohortSegments.length + 1;
  }

  const input = {
    name: form.name,
    description: form.description,
    allocationRate: form.allocationRate,
    isDynamic: true,
    treatments: [
      ...otherCohortTreatments,
      {
        classesOverrides: form.treatments,
        segmentId,
        // Keep the EXISTING treatment id: treatment ids are referenced by offer links and by analytics on
        // served treatments, so a class edit must not regenerate the id (a blank id makes the backend mint
        // a new one). Blank only when this segment has no treatment yet.
        id: treatment.value?.id ?? "",
        ...(treatment.value?.shopId ? { shopId: treatment.value.shopId } : {}),
        ...(treatment.value?.offers?.length ? { offers: treatment.value.offers } : {}),
        treatmentTypes,
        rank,
      },
    ],
  };

  const response = await save({
    gameId,
    versionId,
    abtestId,
    cohortId,
    input,
  });
  const data = useMutationResponse(response, (resp) => resp?.saveCohort);
  if (data?.success) {
    router.push(backLink);
  }
};
</script>

<style lang="scss">
.class-selection-option {
  &.locked {
    color: $red-alert;

    svg {
      color: $red-alert;
      margin-right: size("XS");
    }
  }
}

.add-instance-button-wrapper {
  background: var(--vd-color-text-light-9);
  border: 1px solid var(--vd-color-text-light-7);
  border-top: none;
  padding: 0.5rem;
}

.cohort-form-page {
  .vd-error-component {
    margin-top: 30%;
    max-width: 100%;
  }

  .el-tabs--left .el-tabs__header.is-left {
    width: 20%;
    min-width: 200px;
  }

  .el-tabs--left .el-tabs__item.is-left {
    @include ellipsis;
  }

  .class-selection {
    display: flex;
    align-items: center;

    .add-class-button {
      margin-left: size("M");
    }
  }
  .shop-overrides-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .p-dropdown {
      width: 300px;
    }
  }
}
</style>
